import { isValid, parse } from "date-fns";

/**
 * Reading a spreadsheet's Pickup Date and Pickup Time (DEV-1215).
 *
 * Shared, and it has to be. These rules were written for the edit modal, then wanted again by the
 * import report, and the second copy went in without the two-digit-year handling — which is how a row
 * dated "30/8/26" came to be timestamped **(GMT+10:04:52)**: parsed as the year 26 AD, and formatted
 * against Sydney's Local Mean Time, from before standard timezones existed. One definition, one place.
 */

/** The template's documented date format, and what the backend parses first. */
export const DATE_FORMAT = "dd/MM/yyyy";

/**
 * Every shape a Pickup Date cell can legitimately arrive in.
 *
 * Mirrors `BulkBookingRowService::DATE_FORMATS`. Reading only the canonical form meant a row the
 * server had accepted — "2026-08-30", say — opened in the editor with an EMPTY date picker, and
 * saving wrote that emptiness back over a perfectly good value.
 */
const DATE_INPUT_FORMATS = [DATE_FORMAT, "d/M/yyyy", "dd-MM-yyyy", "yyyy-MM-dd"];

/**
 * "30/8/26" → "30/8/2026", mirroring `BulkBookingRowService::expandTwoDigitYear`.
 *
 * It has to happen BEFORE parsing, in both languages, for the same reason: neither date-fns nor PHP
 * refuses a short year against a four-digit pattern. `dd/MM/yyyy` reads "30/8/26" as 26 AD and hands
 * back a perfectly valid Date, so no amount of trying formats in a different order helps — the first
 * one always matches, wrongly.
 *
 * Always 20xx: a bulk import is near-future travel, so nobody means 1926.
 */
const TWO_DIGIT_YEAR = /^(\d{1,2})([/-])(\d{1,2})\2(\d{2})$/;

export const expandTwoDigitYear = (value: string) => {
  const parts = TWO_DIGIT_YEAR.exec(value);

  return parts ? `${parts[1]}${parts[2]}${parts[3]}${parts[2]}20${parts[4]}` : value;
};

/** The first format that reads the cell, or null when none does. */
export const parsePickupDate = (value: string): Date | null => {
  const trimmed = expandTwoDigitYear(value.trim());
  if (!trimmed) return null;

  for (const pattern of DATE_INPUT_FORMATS) {
    const parsed = parse(trimmed, pattern, new Date());
    if (isValid(parsed)) return parsed;
  }

  return null;
};

/**
 * A 24-hour clock reading as hour + meridiem — 21 becomes 9 PM, 0 becomes 12 AM.
 *
 * The template documents a 12-hour time, but `BulkBookingRowService::TIME_FORMATS` also parses `H:i`
 * and `G:i`, so a spreadsheet holding "21:30" validates and imports perfectly well. It is only ever a
 * problem on the way back out: the editor splits it into an hour box and an AM/PM select, and "21 :
 * 30 AM" matches none of the server's formats.
 */
export const toTwelveHour = (hour: number): { hour: number; meridiem: string } => {
  if (hour === 0) return { hour: 12, meridiem: "AM" };
  if (hour === 12) return { hour: 12, meridiem: "PM" };
  if (hour > 12) return { hour: hour - 12, meridiem: "PM" };

  return { hour, meridiem: "AM" };
};

/** Hour, minute and meridiem from a cell, with a 24-hour reading converted. */
export const parsePickupTime = (value: string) => {
  const match = /^\s*(\d{1,2})[:.]?(\d{2})?\s*(AM|PM)?\s*$/i.exec(value);
  if (!match) return { hour: "", minute: "", meridiem: "AM" };

  const written = match[3]?.toUpperCase();
  const hour = parseInt(match[1] ?? "", 10);

  // An explicit AM/PM is the author's own word for it and is left alone. Without one the value is a
  // 24-hour reading, which is how the server parses it too — so "07:00" is 7 AM and "19:00" is 7 PM.
  const converted = written || Number.isNaN(hour) ? { hour, meridiem: written ?? "AM" } : toTwelveHour(hour);

  return {
    hour: Number.isNaN(converted.hour) ? "" : String(converted.hour),
    minute: match[2] ?? "00",
    meridiem: converted.meridiem,
  };
};

/**
 * A pickup time as the design writes it — "7:30pm".
 *
 * Normalised rather than echoed, so two rows meaning the same thing don't read differently: a file
 * holding "21:30" on one line and "10:00 AM" on the next used to show exactly that, side by side in
 * the same column. An unreadable cell is passed through untouched — that is the user's own text, and
 * showing it is how they see what needs fixing.
 */
export const formatPickupTime = (value: string) => {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return "";

  const { hour, minute, meridiem } = parsePickupTime(trimmed);
  if (!hour) return trimmed;

  return `${hour}:${minute.padStart(2, "0")}${meridiem.toLowerCase()}`;
};

/** A pickup date in the documented format, or the raw cell when it can't be read. */
export const formatPickupDate = (value: string) => {
  const trimmed = (value ?? "").trim();
  const parsed = parsePickupDate(trimmed);

  return parsed ? formatDate(parsed) : trimmed;
};

/** Wrapped so the one place that needs the format string doesn't have to import date-fns twice. */
const formatDate = (date: Date) =>
  `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
